/**
 * Client for attendance (الحضور والانصراف) — see docs/PRD-attendance.md.
 *
 * Every wall-clock value the API hands back is already on the SHOP's clock
 * (`HH:MM`, `YYYY-MM-DDTHH:MM`), so nothing here does time-zone arithmetic: a
 * manager reading the board from abroad still sees the shop's own times.
 */
import type {
  AttendanceBoard,
  AttendanceImportResult,
  AttendanceMe,
  AttendancePunchAction,
  AttendancePunchResult,
  AttendanceRecord,
  AttendanceRecordCreate,
  AttendanceRecordUpdate,
  AttendanceReport,
  AttendanceSettings,
  AttendanceSettingsUpdate,
  Holiday,
  HolidayCreate,
  HolidayUpdate,
  WorkSchedule,
  WorkScheduleCreate,
  WorkScheduleUpdate,
} from '@erp/shared';
import { isAxiosError } from 'axios';

import { api } from './api.js';

/** `undefined` = everyone the caller may see, `'none'` = people with no home branch. */
export type BranchFilter = string | 'none' | undefined;

function branchQuery(q: URLSearchParams, branch: BranchFilter): void {
  if (branch) q.set('warehouseId', branch);
}

export async function getMyAttendance(): Promise<AttendanceMe> {
  return (await api().get<AttendanceMe>('/hr/attendance/me')).data;
}

export async function clockSelf(action: AttendancePunchAction): Promise<AttendancePunchResult> {
  return (await api().post<AttendancePunchResult>(`/hr/attendance/me/clock-${action}`, {})).data;
}

export async function getAttendanceDay(date: string | undefined, branch?: BranchFilter): Promise<AttendanceBoard> {
  const q = new URLSearchParams();
  if (date) q.set('date', date);
  branchQuery(q, branch);
  const qs = q.toString();
  return (await api().get<AttendanceBoard>(`/hr/attendance/day${qs ? `?${qs}` : ''}`)).data;
}

export async function getAttendanceReport(
  from: string, to: string, opts?: { branch?: BranchFilter; employeeId?: string },
): Promise<AttendanceReport> {
  const q = new URLSearchParams({ from, to });
  branchQuery(q, opts?.branch);
  if (opts?.employeeId) q.set('employeeId', opts.employeeId);
  return (await api().get<AttendanceReport>(`/hr/attendance/report?${q.toString()}`)).data;
}

export async function punchEmployee(employeeId: string, action?: AttendancePunchAction): Promise<AttendancePunchResult> {
  return (await api().post<AttendancePunchResult>('/hr/attendance/punch', { employeeId, action })).data;
}

export async function kioskPunch(code: string, pin?: string): Promise<AttendancePunchResult> {
  return (await api().post<AttendancePunchResult>('/hr/attendance/kiosk/punch', { code, pin })).data;
}

export async function createAttendanceRecord(input: AttendanceRecordCreate): Promise<AttendanceRecord> {
  return (await api().post<AttendanceRecord>('/hr/attendance/records', input)).data;
}

export async function updateAttendanceRecord(id: string, input: AttendanceRecordUpdate): Promise<AttendanceRecord> {
  return (await api().patch<AttendanceRecord>(`/hr/attendance/records/${id}`, input)).data;
}

export async function deleteAttendanceRecord(id: string): Promise<void> {
  await api().delete(`/hr/attendance/records/${id}`);
}

export async function getAttendanceSettings(): Promise<AttendanceSettings> {
  return (await api().get<AttendanceSettings>('/hr/attendance/settings')).data;
}

export async function saveAttendanceSettings(input: AttendanceSettingsUpdate): Promise<AttendanceSettings> {
  return (await api().put<AttendanceSettings>('/hr/attendance/settings', input)).data;
}

export async function listWorkSchedules(): Promise<WorkSchedule[]> {
  return (await api().get<{ items: WorkSchedule[] }>('/hr/work-schedules')).data.items;
}

export async function createWorkSchedule(input: WorkScheduleCreate): Promise<WorkSchedule> {
  return (await api().post<WorkSchedule>('/hr/work-schedules', input)).data;
}

export async function updateWorkSchedule(id: string, input: WorkScheduleUpdate): Promise<WorkSchedule> {
  return (await api().patch<WorkSchedule>(`/hr/work-schedules/${id}`, input)).data;
}

export async function deleteWorkSchedule(id: string): Promise<void> {
  await api().delete(`/hr/work-schedules/${id}`);
}

export async function listHolidays(year?: number): Promise<Holiday[]> {
  return (await api().get<{ items: Holiday[] }>(`/hr/holidays${year ? `?year=${year}` : ''}`)).data.items;
}

export async function createHoliday(input: HolidayCreate): Promise<Holiday> {
  return (await api().post<Holiday>('/hr/holidays', input)).data;
}

export async function updateHoliday(id: string, input: HolidayUpdate): Promise<Holiday> {
  return (await api().patch<Holiday>(`/hr/holidays/${id}`, input)).data;
}

export async function deleteHoliday(id: string): Promise<void> {
  await api().delete(`/hr/holidays/${id}`);
}

export async function setAttendancePin(employeeId: string, pin: string | null): Promise<void> {
  await api().put(`/hr/employees/${employeeId}/attendance-pin`, { pin });
}

/**
 * The machine-readable reason behind a refusal (`cause.code` on the problem,
 * or the first field error's code), for mapping onto a sentence.
 */
export function attendanceErrorCode(err: unknown): string | null {
  if (!isAxiosError(err)) return null;
  const data = err.response?.data as { cause?: { code?: string }; errors?: Array<{ code?: string }>; code?: string } | undefined;
  return data?.cause?.code ?? data?.errors?.[0]?.code ?? data?.code ?? null;
}

/** Minutes as `h:mm` — the one duration format every language here reads the same way. */
export function formatMinutes(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
}

/** Today on the browser's calendar, `YYYY-MM-DD` — only a fallback until the API says what the shop's today is. */
export function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Calendar arithmetic on `YYYY-MM-DD`, clock-free. */
export function shiftDate(date: string, days: number): string {
  const t = new Date(`${date}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + days);
  return t.toISOString().slice(0, 10);
}

/**
 * Send a fingerprint device's punch log (xlsx, csv or the device's own .dat) as
 * the raw body. `dryRun` previews; nothing is written until it is false.
 */
export async function importAttendance(
  file: ArrayBuffer,
  opts: { dryRun: boolean; dateOrder: 'dmy' | 'mdy'; useState: boolean },
): Promise<AttendanceImportResult> {
  return (await api().post<AttendanceImportResult>('/hr/attendance/import', file, {
    params: { dryRun: opts.dryRun ? '1' : '0', dateOrder: opts.dateOrder, useState: opts.useState ? '1' : '0' },
    headers: { 'Content-Type': 'application/octet-stream' },
  })).data;
}
